"use client";

import { usePathname, useSearchParams } from "next/navigation";
import React, { useMemo } from "react";

import { useTranslations } from "next-intl";

import { DealType } from "@/enums/dealType";
import { useUnifiedListing } from "@/hooks/useUnifiedListing";
import { Link } from "@/i18n/routing";
import { Filter, RoomsType } from "@/types/SearchFilter.types";
import { EmptyState } from "@/ui/Common/EmptyState";
import Loader from "@/ui/Common/Loader";
import { ObjectCard } from "@/ui/Common/ObjectCard";
import { ObjectList } from "@/ui/Common/ObjectList";
import { PaginationContainer } from "@/ui/Common/Pagination";
import SeoTextDisplay from "@/ui/Common/SeoTextDisplay";
import { SearchPanel } from "@/ui/SearchPanel";
import { SearchResultHeader } from "@/ui/SearchResultHeader";
import { normalizeUrlSpaces, removeLocalePrefix } from "@/utils/urlHelpers";

interface CatalogPageProps {
  landingData: {
    url: string;
    meta: {
      title: string;
      description: string;
      h1: string;
      canonical: string;
      hreflangs: Array<{ lang: string; url: string }>;
    };
    text: string;
    relatedLinks: Array<{
      title: string;
      url: string | null;
      isGroupTitle: boolean;
    }>;
    landing: {
      _id: string;
      dealType: DealType;
      objectType: string;
      city: string;
      district?: string;
      microdistrict?: string;
      street?: string;
      rooms?: number[];
    };
    language: string;
  };
  values?: any; // Server data
}

export default function CatalogPage({ landingData, values }: CatalogPageProps) {
  const t = useTranslations();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const dealType =
    landingData.landing?.dealType ??
    Object.values(DealType).find((dT) => pathname.includes(dT)) ??
    DealType.PRIMARY;

  // Extract initial filters from landing data
  const initialFilters = useMemo(() => {
    const city = landingData.landing?.city || "";
    const district = landingData.landing?.district || "";
    const microdistrict = landingData.landing?.microdistrict || "";
    const street = landingData.landing?.street || "";
    const objectType = landingData.landing?.objectType?.toLowerCase() || "";
    // const rooms = landingData.landing?.rooms || [];

    const filters: Partial<Filter> = {
      objectType: objectType ? [objectType] : [],
      city: city ? [city] : [],
    };

    if (landingData.landing?.district && district) {
      filters.district = [district];
    }

    if (landingData.landing?.microdistrict && microdistrict) {
      filters.microdistrict = [microdistrict];
    }

    if (landingData.landing?.street && street) {
      filters.street = [street];
    }

    // if (rooms && rooms.length > 0) {
    //   filters.rooms = rooms as RoomsType[];
    // }

    return filters;
  }, [landingData]);

  // Use the unified listing hook
  const {
    data,
    transformedData,
    isFetching,
    isInitializing,
    currentPage,
    limit,
    total,
    totalPage,
    setPageSize,
    isEmpty,
    viewMode,
    initialFilters: computedInitialFilters,
    localizedLocationString,
  } = useUnifiedListing({
    dealType,
    basePath: pathname,
    values,
    initialFilters,
  });

  // Create SearchPanel compatible initial filters
  const searchPanelFilters = useMemo((): Partial<Filter> => {
    return {
      dealType,
      ...computedInitialFilters,
    };
  }, [computedInitialFilters, dealType]);

  const isLoading = isFetching || !data || isInitializing;

  return (
    <section className="min-h-screen">
      <SearchPanel
        dealType={dealType}
        initialFilters={searchPanelFilters}
        header={landingData.meta.h1}
        localizedLocationString={isLoading ? " " : localizedLocationString}
        isCatalogPage
      />

      {isLoading ? (
        <Loader />
      ) : (
        <>
          <SearchResultHeader
            total={total}
            dealType={dealType}
            pageSize={limit}
            onPageSizeChange={setPageSize}
            isCatalogPage
          />

          <ObjectList isEmpty={isEmpty}>
            <ObjectList.Items viewMode={viewMode}>
              {transformedData.map((item) => (
                <ObjectCard
                  key={item.id || item.customId || item.title}
                  view={viewMode}
                  translations={item.translations}
                  {...item}
                />
              ))}
            </ObjectList.Items>
            <ObjectList.Empty>
              <EmptyState />
            </ObjectList.Empty>
          </ObjectList>

          {!isEmpty && totalPage > 1 && (
            <PaginationContainer
              totalPage={totalPage}
              currentPage={currentPage}
              pathname={pathname}
              searchParams={new URLSearchParams(searchParams?.toString() || "")}
              paginationType="query"
            />
          )}

          {/* SEO Text Display */}
          {landingData.text && <SeoTextDisplay text={landingData.text} />}

          {landingData.relatedLinks && landingData.relatedLinks.length > 0 && (
            <div className="mt-8 px-4 md:px-0">
              <h2 className="text-2xl font-geometria font-semibold mb-6 text-gray-1200">
                {t("Related links")}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {landingData.relatedLinks.map((link, index) => (
                  <React.Fragment key={index}>
                    {link.isGroupTitle ? (
                      <h3 className="text-lg font-geometria font-medium col-span-full mt-6 first:mt-0 text-gray-1200 border-b border-gray-200 pb-2">
                        {link.title}
                      </h3>
                    ) : (
                      <Link
                        // href={getRelatedLinkHref(link.url, searchParams)}
                        // TODO old solution, should remove string below
                        href={removeLocalePrefix(
                          link.url ? `${normalizeUrlSpaces(link.url)}/` : "#",
                        )}
                        className="text-red hover:text-red-medium transition-colors duration-200 hover:underline text-sm font-geometria leading-relaxed py-1 block"
                      >
                        {link.title}
                      </Link>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}
